import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import {
  exportMuestrasToExcel,
  startOfDay,
  endOfDay,
} from "./muestrasExcelExport";
import { useTranslation } from "react-i18next";

const MuestrasExportExcelDialog = ({
  muestras,
  selectedMuestras = [],
  selectedIds = [],
  buttonProps,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("year");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const currentYear = new Date().getFullYear();
  const [exportYear, setExportYear] = useState(String(currentYear));
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [dateFrom, setDateFrom] = useState(`${currentYear}-01-01`);
  const [dateTo, setDateTo] = useState(todayStr);

  const yearOptions = useMemo(() => {
    const ys = new Set([currentYear, currentYear - 1, currentYear - 2]);
    (muestras || []).forEach((m) => {
      [m.FECHA_PETICION, m.FECHA_ENVIO, m.FECHA_RECEPCION].forEach((f) => {
        const d = f ? new Date(f) : null;
        if (d && !Number.isNaN(d.getTime())) ys.add(d.getFullYear());
      });
    });
    return [...ys].sort((a, b) => b - a);
  }, [muestras, currentYear]);

  const rangeInvalid =
    mode === "range" &&
    (Number.isNaN(new Date(dateFrom).getTime()) ||
      Number.isNaN(new Date(dateTo).getTime()) ||
      startOfDay(new Date(dateFrom)).getTime() >
        endOfDay(new Date(dateTo)).getTime());

  const handleExport = async () => {
    let rangeStart;
    let rangeEnd;
    if (mode === "selected") {
      if (!selectedMuestras.length) return;
    } else if (mode === "year") {
      const y = parseInt(exportYear, 10);
      if (Number.isNaN(y)) return;
      rangeStart = startOfDay(new Date(y, 0, 1));
      rangeEnd = endOfDay(new Date(y, 11, 31));
    } else {
      if (rangeInvalid) return;
      rangeStart = startOfDay(new Date(dateFrom));
      rangeEnd = endOfDay(new Date(dateTo));
    }

    const namePart =
      mode === "selected"
        ? `Seleccionadas_${selectedMuestras.length}`
        : mode === "year"
          ? exportYear
          : `${dateFrom}_${dateTo}`;

    setExporting(true);
    setExportError("");
    setProgress({ current: 0, total: 0 });
    try {
      await exportMuestrasToExcel({
        muestras: mode === "selected" ? selectedMuestras : muestras,
        rangeStart,
        rangeEnd,
        ignorePeriod: mode === "selected",
        filename: `Muestras_${namePart}.xlsx`,
        onProgress: setProgress,
      });
      setOpen(false);
    } catch (err) {
      console.error("Error exportando muestras:", err);
      setExportError(t("Samples.excelExportError"));
    } finally {
      setExporting(false);
    }
  };

  const progressLabel =
    progress.total > 0
      ? t("Samples.excelLoadingPhotos", {
          current: progress.current,
          total: progress.total,
        })
      : t("Samples.excelPreparing");

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          setMode(selectedIds.length ? "selected" : "year");
          setExportError("");
          setOpen(true);
        }}
        disabled={exporting}
        {...buttonProps}
      >
        {t("Samples.exportExcel")}
      </Button>
      <Dialog
        open={open}
        onClose={() => !exporting && setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("Samples.exportExcelTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("Samples.exportExcelDescription")}
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">{t("Samples.excelPeriod")}</FormLabel>
            <RadioGroup
              row
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <FormControlLabel
                value="year"
                control={<Radio size="small" />}
                label={t("Samples.excelByYear")}
                disabled={exporting}
              />
              <FormControlLabel
                value="range"
                control={<Radio size="small" />}
                label={t("Samples.excelDateRange")}
                disabled={exporting}
              />
              <FormControlLabel
                value="selected"
                control={<Radio size="small" />}
                label={t("Samples.excelSelectedInGrid", {
                  count: selectedIds.length,
                })}
                disabled={exporting || selectedIds.length === 0}
              />
            </RadioGroup>
          </FormControl>

          {mode === "selected" ? (
            <Typography
              variant="body2"
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              {t("Samples.excelSelectedInfo", {
                count: selectedMuestras.length,
              })}
            </Typography>
          ) : mode === "year" ? (
            <TextField
              select
              fullWidth
              label={t("Samples.excelYear")}
              size="small"
              value={exportYear}
              onChange={(e) => setExportYear(e.target.value)}
              disabled={exporting}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={String(y)}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label={t("Samples.excelFrom")}
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                disabled={exporting}
              />
              <TextField
                label={t("Samples.excelTo")}
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                disabled={exporting}
                helperText={t("Samples.excelToTodayHint")}
              />
              {rangeInvalid && (
                <Typography color="error" variant="caption">
                  {t("Samples.excelInvalidRange")}
                </Typography>
              )}
            </Box>
          )}

          {exportError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {exportError}
            </Typography>
          )}

          {exporting && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {progressLabel}
              </Typography>
              <LinearProgress
                variant={progress.total > 0 ? "determinate" : "indeterminate"}
                value={
                  progress.total > 0
                    ? (progress.current / progress.total) * 100
                    : 0
                }
                sx={{ mt: 1 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={exporting}>
            {t("Samples.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={
              rangeInvalid ||
              exporting ||
              (mode === "selected" && selectedMuestras.length === 0)
            }
            startIcon={
              exporting ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {exporting
              ? t("Samples.excelGenerating")
              : t("Samples.excelDownload")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MuestrasExportExcelDialog;
